/**
 * <scroll-sequence> — reproduce una secuencia de imágenes con el scroll.
 *
 * Diseñado para portarse al tema Horizon tal cual: es un custom element, que es
 * la arquitectura que ya usa el tema.
 *
 * Principios:
 *  - El scroll nunca dibuja. Solo anota la posición; dibuja rAF. Así el hilo
 *    principal no se bloquea aunque lleguen cientos de eventos.
 *  - Se dibuja únicamente cuando cambia el índice de frame. Redibujar el mismo
 *    frame es el error clásico que hace que estas animaciones vayan a tirones.
 *  - Nada corre fuera de pantalla: un IntersectionObserver apaga el bucle.
 *  - Degrada. Si no hay JS, si el usuario pidió menos movimiento, o si las
 *    imágenes fallan, queda una imagen fija y el texto legible.
 *
 * Uso:
 *   <scroll-sequence manifest="assets/frames/x.json" scrub="3"></scroll-sequence>
 */

const PREFERS_REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const IS_COARSE = matchMedia('(pointer: coarse)');

class ScrollSequence extends HTMLElement {
  #frames = [];        // HTMLImageElement | ImageBitmap | null por índice
  #manifest = null;
  #index = -1;
  #target = 0;         // progreso 0..1 que dicta el scroll
  #eased = 0;          // progreso suavizado que se dibuja
  #raf = null;
  #visible = false;
  #ctx = null;
  #dpr = 1;
  #reservas = null;
  #ready = false;
  #onScroll = null;
  #onResize = null;

  connectedCallback() {
    this.#build();
    this.#load().catch((err) => {
      console.warn('[scroll-sequence] no se pudo cargar la secuencia:', err);
      this.dataset.state = 'failed';
    });
  }

  disconnectedCallback() {
    this.#stop();
    this.io?.disconnect();
    removeEventListener('scroll', this.#onScroll);
    removeEventListener('resize', this.#onResize);
  }

  /* ------------------------------------------------------------ público -- */

  /**
   * Pinta un progreso concreto (0..1) de forma síncrona, sin esperar al rAF.
   * Lo usa el editor de temas de Shopify para repintar al cambiar un ajuste,
   * y las pruebas para verificar sin depender del bucle de animación.
   */
  renderAt(progress) {
    const p = Math.min(1, Math.max(0, Number(progress) || 0));
    this.#target = p;
    this.#eased = p;
    this.#draw(p, true);
    this.#updateBeats(p);
    this.style.setProperty('--progress', p.toFixed(4));
    return this.frameIndex;
  }

  /** Índice del frame dibujado ahora mismo. */
  get frameIndex() {
    return this.#index;
  }

  get frameCount() {
    return this.#frames.length;
  }

  /* ---------------------------------------------------------------- DOM -- */

  #build() {
    this.track = this.querySelector('[data-track]');
    this.stage = this.querySelector('[data-stage]');
    this.canvas = this.querySelector('canvas');
    this.beatEls = [...this.querySelectorAll('[data-beat]')];

    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.stage?.prepend(this.canvas);
    }
    this.canvas.setAttribute('role', 'img');
    this.#ctx = this.canvas.getContext('2d', { alpha: false });
    this.dataset.state = 'loading';
  }

  /* ------------------------------------------------------------- carga -- */

  async #load() {
    const src = this.getAttribute('manifest');
    const inline = this.querySelector('script[type="application/json"]');
    this.#manifest = inline ? JSON.parse(inline.textContent) : await (await fetch(src)).json();

    const { frames, basePath = '' } = this.#manifest;
    // basePath se resuelve contra el manifiesto, no contra la página: si no,
    // mover la página de carpeta rompe todas las rutas de los frames.
    const root = new URL(basePath, new URL(src || location.href, location.href));
    // En táctil se salta frames: la mitad de descarga y el ojo no lo nota
    // porque la pantalla es pequeña.
    const stride = IS_COARSE.matches ? (this.#manifest.mobileStride || 1) : 1;
    this.urls = frames.filter((_, i) => i % stride === 0).map((f) => new URL(f, root).href);
    this.#frames = new Array(this.urls.length).fill(null);

    this.canvas.setAttribute(
      'aria-label',
      this.getAttribute('alt') || 'Secuencia del producto girando'
    );

    // El texto de los beats vive en el manifiesto junto a su posición en la
    // secuencia. Lo rellena el componente para que cambiar el copy no obligue
    // a tocar el marcado de cada página que use la secuencia.
    (this.#manifest.beats || []).forEach((b, i) => {
      if (this.beatEls[i] && !this.beatEls[i].textContent.trim()) {
        this.beatEls[i].textContent = b.text;
      }
    });

    // El primer frame se carga solo y se pinta: la sección deja de estar vacía
    // lo antes posible.
    await this.#loadFrame(0);
    this.#ready = true;
    this.dataset.state = 'ready';
    // La proporción del lienzo la fija el propio fotograma. En el escenario fijo
    // no cambia nada —ahí el lienzo va a `inset: 0`— pero en el modo sin
    // animación el lienzo fluye, y con una proporción escrita a mano en CSS un
    // fotograma vertical quedaba enmarcado en negro. Además el cliente puede
    // subir otros al tema, así que suponerla es garantizar que se desajuste.
    const primero = this.#frames[0];
    if (primero?.naturalWidth) {
      this.canvas.style.aspectRatio = `${primero.naturalWidth} / ${primero.naturalHeight}`;
    }
    this.#resize();
    this.#draw(0, true);

    if (PREFERS_REDUCED.matches) {
      // Un solo frame, sin bucle, sin descargar 47 imágenes más.
      this.dataset.state = 'static';
      this.#revealAllBeats();
      return;
    }

    this.#observe();
    this.#loadRest();
  }

  #loadFrame(i) {
    if (this.#frames[i]) return Promise.resolve(this.#frames[i]);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        this.#frames[i] = img;
        resolve(img);
      };
      img.onerror = () => reject(new Error(this.urls[i]));
      img.src = this.urls[i];
    });
  }

  /** Carga el resto en tandas cortas para no saturar la cola de red. */
  async #loadRest() {
    const BATCH = 6;
    const pending = this.urls.map((_, i) => i).filter((i) => !this.#frames[i]);
    for (let i = 0; i < pending.length; i += BATCH) {
      await Promise.allSettled(pending.slice(i, i + BATCH).map((n) => this.#loadFrame(n)));
      // Redibuja por si el usuario ya está sobre un frame que acaba de llegar
      if (this.#visible) this.#index = -1;
    }
    this.dataset.state = 'complete';
  }

  /* ----------------------------------------------------------- scroll --- */

  #observe() {
    this.io = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        this.#visible ? this.#start() : this.#stop();
      },
      { rootMargin: '20% 0px' }
    );
    this.io.observe(this);

    this.#onScroll = () => this.#measure();
    this.#onResize = () => {
      this.#resize();
      this.#measure();
      this.#index = -1;
    };
    addEventListener('scroll', this.#onScroll, { passive: true });
    addEventListener('resize', this.#onResize, { passive: true });
    this.#measure();
  }

  /** Solo mide. No toca el canvas: eso es trabajo del rAF. */
  #measure() {
    const rect = (this.track || this).getBoundingClientRect();
    const travel = rect.height - innerHeight;
    if (travel <= 0) {
      this.#target = 0;
      return;
    }
    this.#target = Math.min(1, Math.max(0, -rect.top / travel));
  }

  #start() {
    if (this.#raf != null) return;
    const tick = () => {
      // Suavizado exponencial: el scrub se siente con inercia en vez de
      // pegarse al dedo. `scrub` alto = más pesado.
      const k = 1 / Math.max(1, Number(this.getAttribute('scrub') || 3));
      this.#eased += (this.#target - this.#eased) * k;
      if (Math.abs(this.#target - this.#eased) < 0.0005) this.#eased = this.#target;

      this.#draw(this.#eased);
      this.#updateBeats(this.#eased);
      this.style.setProperty('--progress', this.#eased.toFixed(4));
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }

  #stop() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  /* ------------------------------------------------------------ pintar -- */

  #resize() {
    const r = this.stage ? this.stage.getBoundingClientRect() : this.getBoundingClientRect();
    // Tope de 2: por encima el costo de pintar sube y nadie ve la diferencia.
    this.#dpr = Math.min(devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(r.width * this.#dpr);
    this.canvas.height = Math.round(r.height * this.#dpr);
    // Las dos reservas se miden aquí y no al pintar: dependen de la maquetación,
    // que solo cambia al redimensionar, y medirlas en cada fotograma obligaría a
    // recalcular el diseño en mitad del scroll.
    this.#reservas = this.#medirReservas(this.canvas.height);
  }

  /**
   * Cuánto se reserva arriba y abajo, en píxeles del lienzo.
   *
   * Las fracciones de CSS son el piso; lo que manda es dónde están de verdad el
   * encabezado y el texto. Se miden porque se mueven: a 1366 px la navegación
   * salta de línea y el encabezado pasa de 73 a 90 px, y los beats cambian de
   * alto con el cuerpo de letra y con la longitud de la frase. Con las
   * fracciones solas quedaban 4 px de holgura justo en el portátil más común.
   */
  #medirReservas(ch) {
    const HOLGURA = 26;   // que el cordón no roce ni la barra ni el texto
    const pisoArriba = ch * this.#fraccion('--sequence-safe-top', 0);
    const pisoAbajo = ch * this.#fraccion('--sequence-safe-bottom', 0.2);
    let arriba = pisoArriba;
    let abajo = pisoAbajo;

    // Un encabezado que no flota no tapa nada, y no hay nada que reservar.
    const enc = document.querySelector('.header') || document.querySelector('header');
    if (enc) {
      const pos = getComputedStyle(enc).position;
      const alto = enc.getBoundingClientRect().height;
      if ((pos === 'sticky' || pos === 'fixed') && alto) {
        arriba = Math.max(pisoArriba, (alto + HOLGURA) * this.#dpr);
      }
    }

    const caja = this.querySelector('.sequence__beats');
    const lienzo = this.canvas.getBoundingClientRect();
    if (caja && lienzo.height) {
      const r = caja.getBoundingClientRect();
      if (r.height) abajo = Math.max(pisoAbajo, (lienzo.bottom - r.top + HOLGURA) * this.#dpr);
    }

    // Topes: entre las dos no pueden dejar el escenario sin banda útil.
    arriba = Math.min(arriba, ch * 0.35);
    abajo = Math.min(abajo, ch * 0.45);
    return { arriba, abajo };
  }

  /** Devuelve el frame cargado más cercano: evita huecos mientras precarga. */
  #nearestLoaded(i) {
    if (this.#frames[i]) return this.#frames[i];
    for (let d = 1; d < this.#frames.length; d++) {
      if (this.#frames[i - d]) return this.#frames[i - d];
      if (this.#frames[i + d]) return this.#frames[i + d];
    }
    return null;
  }

  #draw(progress, force = false) {
    if (!this.#ready) return;
    const last = this.#frames.length - 1;
    const i = Math.min(last, Math.max(0, Math.round(progress * last)));
    if (i === this.#index && !force) return;   // la clave del rendimiento
    this.#index = i;

    const img = this.#nearestLoaded(i);
    if (!img) return;

    const { width: cw, height: ch } = this.canvas;
    const ctx = this.#ctx;
    const estilo = getComputedStyle(this);
    ctx.fillStyle = estilo.getPropertyValue('--sequence-bg') || '#fff';
    ctx.fillRect(0, 0, cw, ch);
    this.#drawGrid(ctx, cw, ch, estilo);

    // El producto se dibuja dentro de una banda, no sobre todo el lienzo.
    //
    // Abajo, porque los beats van ahí y sin la reserva el texto le cae encima.
    // Arriba, porque el escenario es sticky a `top: 0` y el encabezado flota por
    // encima: al agrandar el producto (fov 28) las puntas del cordón se metían
    // debajo de la barra y se perdían. Reservar es más honesto que esconder el
    // encabezado en esta sección, que rompería la navegación justo donde la
    // página es más larga.
    const { arriba, abajo } = this.#reservas || this.#medirReservas(ch);
    const banda = Math.max(1, ch - arriba - abajo);

    // contain: el producto nunca se recorta
    const scale = Math.min(cw / img.naturalWidth, banda / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (cw - w) / 2, arriba + (banda - h) / 2, w, h);
  }

  /**
   * Lee una custom property como fracción de 0 a 0,6.
   *
   * Se compara la cadena vacía y no el número: `Number('')` es 0, así que tratar
   * el 0 como «no definido» haría imposible anular la reserva desde CSS, que es
   * justo lo que hace el modo sin animación.
   */
  #fraccion(nombre, porDefecto) {
    const crudo = getComputedStyle(this).getPropertyValue(nombre).trim();
    const v = crudo === '' ? porDefecto : Number(crudo);
    return Math.min(0.6, Math.max(0, Number.isFinite(v) ? v : porDefecto));
  }

  /**
   * La retícula del sitio, dentro del lienzo.
   *
   * En la página es un `body::before` fijo que va detrás del contenido, así que
   * el fondo opaco de esta sección la tapaba y la secuencia se leía como un
   * parche pegado encima en vez de como parte de la página. Un pseudoelemento
   * sobre el escenario tampoco vale: el lienzo lo cubre entero.
   *
   * Así que se pinta aquí, entre el fondo y el producto. Queda por debajo del
   * producto, que es donde tiene que estar, y el paso coincide con el de la
   * página para que las líneas parezcan las mismas.
   */
  #drawGrid(ctx, cw, ch, estilo) {
    const unidad = parseFloat(estilo.getPropertyValue('--grid-unit')) || 8;
    const paso = unidad * 4 * this.#dpr;
    const linea = estilo.getPropertyValue('--sequence-grid').trim();
    if (!linea || paso < 4) return;

    // Se desvanece hacia abajo: la retícula sugiere el instrumento, no tapiza.
    const fade = ctx.createLinearGradient(0, 0, 0, ch);
    fade.addColorStop(0, linea);
    fade.addColorStop(0.45, linea);
    fade.addColorStop(0.92, 'transparent');

    ctx.save();
    ctx.strokeStyle = fade;
    ctx.lineWidth = 1 * this.#dpr;
    ctx.beginPath();
    for (let x = 0.5; x < cw; x += paso) { ctx.moveTo(x, 0); ctx.lineTo(x, ch); }
    for (let y = 0.5; y < ch; y += paso) { ctx.moveTo(0, y); ctx.lineTo(cw, y); }
    ctx.stroke();
    ctx.restore();
  }

  /* ------------------------------------------------------------- texto -- */

  #updateBeats(progress) {
    const beats = this.#manifest?.beats || [];
    if (!beats.length || !this.beatEls.length) return;
    this.beatEls.forEach((el, n) => {
      const start = beats[n]?.at ?? 0;
      const end = beats[n + 1]?.at ?? 1.02;
      const span = end - start;
      // entra en el primer 18% de su tramo y sale en el último 18%
      const local = (progress - start) / span;
      const isLast = n === this.beatEls.length - 1;
      // Los textos entran y salen en SECUENCIA, no en fundido cruzado: dos
      // frases superpuestas a media opacidad no se leen ninguna de las dos. El
      // anterior termina de irse justo cuando el siguiente empieza a entrar.
      const W = 0.16;                       // tramo de entrada y de salida
      const fadeIn = local / W;
      // El último no se desvanece: es el remate y tiene que seguir legible
      // cuando el usuario llega al final.
      const fadeOut = isLast ? Infinity : (1 - local) / W;
      const opacity = local >= 0 ? Math.min(1, fadeIn, fadeOut) : 0;
      el.style.opacity = Math.max(0, opacity).toFixed(3);
      el.style.transform = `translateY(${((1 - Math.max(0, opacity)) * 12).toFixed(1)}px)`;
      el.setAttribute('aria-hidden', opacity < 0.5 ? 'true' : 'false');
    });
  }

  /** Sin animación, todos los textos quedan visibles y en orden. */
  #revealAllBeats() {
    this.beatEls.forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.setAttribute('aria-hidden', 'false');
    });
  }
}

customElements.define('scroll-sequence', ScrollSequence);
